import type { register, updateCustomer } from '@eliadmin/core/src/api/customers/customers-api';
import type { Country } from '@eliadmin/core/src/api/general/types/general-types';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { splitStreet, toE164Phone } from '@eliadmin/core/src/utils/nt/address';

import type { RegisterFormInput, RegisterFormValues } from './register-schema';

type RegisterBody = Parameters<typeof register>[0];
type CustomerUpdate = Parameters<typeof updateCustomer>[0]['data'];

/** An empty form; the country is the store's default. */
export function registerDefaults(country: string): RegisterFormInput {
  return {
    company_buy: false,
    email: '',
    password: '',
    password_confirmation: '',
    name: '',
    surname: '',
    phone: '',
    company: '',
    ico: '',
    dic: '',
    ic_dph: '',
    address: '',
    city: '',
    postcode: '',
    country,
    other_delivery: false,
    delivery_name: '',
    delivery_contact: '',
    delivery_address: '',
    delivery_city: '',
    delivery_postcode: '',
    delivery_country: country,
    consent: false,
    newsletter: false,
  };
}

/** Body of the core `register` exactly as the core form sends it; the one consent is `tos` + `gdpr`. */
export function toRegisterBody(values: RegisterFormValues, locale: string): RegisterBody {
  return {
    locale,
    name: values.name,
    surname: values.surname,
    email: values.email,
    phone: toE164Phone(values.phone),
    password: values.password,
    password_confirmation: values.password_confirmation,
    tos: values.consent,
    gdpr: values.consent,
    newsletter: values.newsletter,
    newsletter_return_url: new URL(ROUTES.NEWSLETTER_CONFIRM(), env.NEXT_PUBLIC_APP_URL).toString(),
    'g-recaptcha-response': '',
  };
}

/** Registration numbers may be typed in groups („51 234 567“) – stored without the spaces. */
const compact = (value: string) => value.replace(/\s+/g, '');

/** „Peter Novák“ → name „Peter“, surname „Novák“ (the customer record keeps them apart). */
function splitName(value: string) {
  const [name = '', ...rest] = value.split(/\s+/).filter(Boolean);
  return { name, surname: rest.join(' ') };
}

/**
 * The second call after `register` (`PUT /store/customers/update`, the field names of the „Môj
 * profil“ save): what the register request has no place for – the company and its seat, a person's
 * billing address when typed, the delivery address with a company's site and contact on site
 * (`company_delivery`, `name_delivery` + `surname_delivery`). `null` when there is nothing to add.
 * The name, e-mail and phone go along unchanged, as the profile sends them.
 */
export function toCustomerUpdate(
  values: RegisterFormValues,
  countries: Country[],
): CustomerUpdate | null {
  const company = values.company_buy;
  const billing = company || !!(values.address || values.city || values.postcode);
  if (!company && !billing && !values.other_delivery) return null;

  const seat = splitStreet(values.address);
  const delivery = splitStreet(values.delivery_address);
  const contact = splitName(values.delivery_contact);

  return {
    name: values.name,
    surname: values.surname,
    email: values.email,
    phone: toE164Phone(values.phone),
    company_buy: company,
    ...(company
      ? {
          company: values.company,
          ico: compact(values.ico),
          dic: compact(values.dic),
          ic_dph: compact(values.ic_dph).toUpperCase(),
        }
      : {}),
    ...(billing
      ? {
          address: seat.street,
          street_number: seat.number,
          city: values.city,
          postcode: values.postcode,
          country: {
            id: values.country,
            name: countries.find(country => country.id === values.country)?.name ?? values.country,
          },
        }
      : {}),
    ...(values.other_delivery
      ? {
          address_delivery: delivery.street,
          street_number_delivery: delivery.number,
          city_delivery: values.delivery_city,
          postcode_delivery: values.delivery_postcode,
          country_delivery: { id: values.delivery_country },
          ...(company
            ? {
                company_delivery: values.delivery_name,
                name_delivery: contact.name,
                surname_delivery: contact.surname,
              }
            : {}),
        }
      : {}),
  };
}
